import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  LockKeyhole,
} from 'lucide-react';

const inputClass = (error, locked = false) =>
  `h-12 w-full rounded-xl border px-4 text-sm outline-none transition ${
    error
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
      : locked
        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  }`;

const FieldError = ({ message }) =>
  message ? (
    <p className="mt-1 text-xs font-medium text-red-600">
      {message}
    </p>
  ) : null;

const Label = ({ children, required = false, optional = false }) => (
  <label className="mb-2 block text-sm font-medium text-gray-700">
    {children}
    {required && <span className="text-red-500"> *</span>}
    {optional && (
      <span className="font-normal text-gray-400"> (Optional)</span>
    )}
  </label>
);

const StaffEmploymentDetails = ({
  formData,
  handleChange,
  errors = {},
}) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between border-b border-gray-100 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
            2
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Employment Details
            </h2>
            <p className="text-xs text-gray-500">
              Department, designation, joining date, and employment status.
            </p>
          </div>
        </div>

        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label>Employee ID</Label>
            <div className="relative">
              <input
                value={formData.employeeId || 'Generated automatically'}
                disabled
                className={`${inputClass(false, true)} pr-10`}
              />
              <LockKeyhole className="absolute right-3 top-3.5 h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              System-generated and cannot be edited.
            </p>
          </div>

          <div>
            <Label required>Staff Type</Label>
            <select
              name="staffType"
              value={formData.staffType}
              onChange={handleChange}
              className={inputClass(errors.staffType)}
            >
              <option value="">Select staff type</option>
              <option value="TEACHING">Teaching</option>
              <option value="NON_TEACHING">Non-Teaching</option>
            </select>
            <FieldError message={errors.staffType} />
          </div>

          <div>
            <Label required>Designation</Label>
            <input
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder="Enter designation"
              className={inputClass(errors.designation)}
            />
            <FieldError message={errors.designation} />
          </div>

          <div>
            <Label required>Department</Label>
            <input
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder="Enter department"
              className={inputClass(errors.department)}
            />
            <FieldError message={errors.department} />
          </div>

          <div>
            <Label optional>Qualification</Label>
            <input
              name="qualification"
              value={formData.qualification}
              onChange={handleChange}
              placeholder="Enter qualification"
              className={inputClass(errors.qualification)}
            />
            <FieldError message={errors.qualification} />
          </div>

          <div>
            <Label required>Joining Date</Label>
            <input
              type="date"
              name="joiningDate"
              value={formData.joiningDate}
              onChange={handleChange}
              className={inputClass(errors.joiningDate)}
            />
            <FieldError message={errors.joiningDate} />
          </div>

          <div>
            <Label required>Status</Label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={inputClass(errors.status)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
            <FieldError message={errors.status} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffEmploymentDetails;
